/**
 * POC animated lobby — the one deliberately cheap ambient: a light band sweeps across the
 * navigation bar every ~20 s. One element, transform only, a finite WAAPI animation re-armed by a
 * setTimeout; the timer is cleared and the animation cancelled while the effects bus is suspended
 * (idle manager), so nothing ticks behind a game. Renders nothing with the flag off.
 */

import React, { useEffect, useRef } from 'react';
import { effectsBus } from './effectsBus';
import { FX_ENABLED } from './fxFlag';
import { getMotionTier } from './motionTier';

const SWEEP_PERIOD_MS = 20_000;
const SWEEP_DURATION_MS = 900;

const NavSweep: React.FC = () => {
  const bandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!FX_ENABLED) return undefined;

    let timer: number | null = null;
    let animation: Animation | null = null;
    let stopped = false;

    const arm = (): void => {
      if (stopped || timer !== null) return;
      timer = window.setTimeout(run, SWEEP_PERIOD_MS);
    };

    const run = (): void => {
      timer = null;
      const band = bandRef.current;
      if (stopped) return;
      if (!band || getMotionTier() === 'off' || typeof band.animate !== 'function') {
        arm();
        return;
      }
      animation = band.animate(
        [{ transform: 'translateX(-120%)' }, { transform: 'translateX(460%)' }],
        { duration: SWEEP_DURATION_MS, easing: 'ease-in-out' },
      );
      animation.onfinish = () => {
        animation = null;
        arm();
      };
    };

    const stop = (): void => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      animation?.cancel();
      animation = null;
    };

    const start = (): void => {
      stopped = false;
      arm();
    };

    const unsubscribe = effectsBus.subscribeSuspended((suspended) => (suspended ? stop() : start()));
    if (!effectsBus.isSuspended()) start();

    return () => {
      unsubscribe();
      stop();
    };
  }, []);

  if (!FX_ENABLED) return null;

  return (
    <div className="fx-sweep-clip" aria-hidden="true">
      <div ref={bandRef} className="fx-sweep-band" />
    </div>
  );
};

export default NavSweep;
